"use client";

import {
  Children,
  isValidElement,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import useLatest from "@/hooks/utility/useLatest";
import {
  LuInbox,
  LuRefreshCw,
  LuTriangleAlert,
  LuWifiOff,
} from "react-icons/lu";

import { Button } from "@/components/ui/button";
import {
  Case,
  If,
  Switch,
} from "@/components/ui/conditional";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type AsyncState =
  | "idle"
  | "pending"
  | "refetching"
  | "error"
  | "empty"
  | "paused"
  | "success";

/**
 * Structural, TanStack-compatible query shape. Deliberately NOT importing
 * `@tanstack/react-query` so this package stays dependency-light — a
 * `useQuery` / `useInfiniteQuery` result satisfies this interface.
 */
interface QueryLike<TData> {
  data: TData | undefined;
  error: unknown;
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  fetchStatus: "fetching" | "paused" | "idle";
  refetch: () => unknown;
}

type NodeOrFn<TArgs extends unknown[] = []> =
  | ReactNode
  | ((...args: TArgs) => ReactNode);

type ResolvedState<TData> =
  | { state: "idle" }
  | { state: "pending" }
  | { state: "paused" }
  | { state: "error"; error: unknown }
  | { state: "empty"; data: NonNullable<TData> }
  | { state: "refetching"; data: NonNullable<TData> }
  | { state: "success"; data: NonNullable<TData> };

// ---------------------------------------------------------------------------
// State resolver (pure — the prime unit-test target)
// ---------------------------------------------------------------------------

function resolveState<TData>(
  query: QueryLike<TData>,
  isEmpty?: (data: NonNullable<TData>) => boolean,
): ResolvedState<TData> {
  if (query.isError) {
    return { state: "error", error: query.error };
  }

  const hasData = query.data !== undefined && query.data !== null;

  if (query.fetchStatus === "paused" && !hasData) {
    return { state: "paused" };
  }

  if (query.isPending && !hasData) {
    return { state: "pending" };
  }

  if (query.fetchStatus === "idle" && !hasData) {
    return { state: "idle" };
  }

  if (!hasData) {
    return { state: "pending" };
  }

  const data = query.data as NonNullable<TData>;

  if (isEmpty?.(data) === true) {
    return { state: "empty", data };
  }

  if (query.isFetching) {
    return { state: "refetching", data };
  }

  return { state: "success", data };
}

// ---------------------------------------------------------------------------
// Slot markers (mirror conditional.tsx — render nothing on their own)
// ---------------------------------------------------------------------------

interface SlotProps<TChildren> {
  children: TChildren;
}

function slotWarning(name: string): ReactNode {
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      `[AsyncBoundary] <AsyncBoundary.${name}> must be a direct child of <AsyncBoundary>. Rendering nothing.`,
    );
  }
  return null;
}

function Pending(_props: SlotProps<NodeOrFn>): ReactNode {
  return slotWarning("Pending");
}
function Idle(_props: SlotProps<NodeOrFn>): ReactNode {
  return slotWarning("Idle");
}
function Paused(_props: SlotProps<NodeOrFn>): ReactNode {
  return slotWarning("Paused");
}
function Refetching(_props: SlotProps<NodeOrFn>): ReactNode {
  return slotWarning("Refetching");
}
function ErrorSlot(
  _props: SlotProps<NodeOrFn<[error: unknown, retry: () => unknown]>>,
): ReactNode {
  return slotWarning("Error");
}
function EmptySlot<TData>(_props: SlotProps<NodeOrFn<[data: TData]>>): ReactNode {
  return slotWarning("Empty");
}
function Success<TData>(_props: SlotProps<NodeOrFn<[data: TData]>>): ReactNode {
  return slotWarning("Success");
}

const SLOT_TYPES = new Map<unknown, AsyncState>([
  [Pending, "pending"],
  [Idle, "idle"],
  [Paused, "paused"],
  [Refetching, "refetching"],
  [ErrorSlot, "error"],
  [EmptySlot, "empty"],
  [Success, "success"],
]);

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// biome-ignore lint/suspicious/noExplicitAny: internal — external API is typed per slot
function callNodeOrFn(value: NodeOrFn<any>, ...args: unknown[]): ReactNode {
  return typeof value === "function" ? value(...args) : value;
}

function extractMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  if (typeof error === "string" && error.length > 0) {
    return error;
  }
  return "An unexpected error occurred.";
}

function collectSlots(children: ReactNode) {
  const slots = new Map<AsyncState, NodeOrFn>();
  let hasAnySlot = false;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }
    const state = SLOT_TYPES.get(child.type);
    if (state === undefined) {
      return;
    }
    hasAnySlot = true;
    if (process.env.NODE_ENV !== "production" && slots.has(state)) {
      console.warn(
        `[AsyncBoundary] Duplicate <AsyncBoundary> slot for "${state}". Using the first.`,
      );
      return;
    }
    slots.set(state, (child.props as SlotProps<NodeOrFn>).children);
  });

  return { slots, hasAnySlot };
}

const ANNOUNCEMENTS: Record<AsyncState, string> = {
  idle: "",
  pending: "Loading…",
  refetching: "Updating…",
  error: "Failed to load content.",
  empty: "No results found.",
  paused: "You are offline. Waiting for a connection.",
  success: "Content loaded.",
};

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

function DefaultPending({
  className,
  title = "Loading…",
  description = "Please wait while we load the content.",
}: {
  className?: string;
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <Empty
      aria-busy="true"
      className={className}
      data-slot="async-boundary-pending"
      role="status"
    >
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Spinner />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <If condition={description != null}>
          <EmptyDescription>{description}</EmptyDescription>
        </If>
      </EmptyHeader>
    </Empty>
  );
}

function DefaultError({
  error,
  retry,
  retryRef,
  title = "Something went wrong",
  description,
}: {
  error: unknown;
  retry: () => unknown;
  retryRef: React.RefObject<HTMLButtonElement | null>;
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <Empty data-slot="async-boundary-error" role="alert">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <LuTriangleAlert />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>
          {description ?? extractMessage(error)}
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button
          onClick={() => retry()}
          ref={retryRef}
          size="sm"
          variant="outline"
        >
          <LuRefreshCw />
          Retry
        </Button>
      </EmptyContent>
    </Empty>
  );
}

function DefaultEmpty({
  title = "Nothing here yet",
  description = "There is no data to display right now.",
}: {
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <Empty data-slot="async-boundary-empty">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <LuInbox />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <If condition={description != null}>
          <EmptyDescription>{description}</EmptyDescription>
        </If>
      </EmptyHeader>
    </Empty>
  );
}

function DefaultPaused({
  title = "You're offline",
  description = "Reconnect to load this content.",
}: {
  title?: ReactNode;
  description?: ReactNode;
}) {
  return (
    <Empty data-slot="async-boundary-paused">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <LuWifiOff />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <If condition={description != null}>
          <EmptyDescription>{description}</EmptyDescription>
        </If>
      </EmptyHeader>
      <EmptyContent />
    </Empty>
  );
}

function RefetchOverlay({ indicator }: { indicator: NodeOrFn | undefined }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-2 right-2 z-10"
      data-slot="async-boundary-refetch-indicator"
    >
      {indicator === undefined ? <Spinner /> : callNodeOrFn(indicator)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AsyncBoundary
// ---------------------------------------------------------------------------

interface DefaultSlotText {
  title?: ReactNode;
  description?: ReactNode;
}

/**
 * Override the copy of the built-in default UIs without replacing them via a
 * full slot. Ignored for any state that has an explicit `<AsyncBoundary.*>`
 * slot. `idle`/`refetching`/`success` have no default copy, so they are absent.
 */
interface AsyncBoundaryDefaults {
  pending?: DefaultSlotText;
  error?: DefaultSlotText;
  empty?: DefaultSlotText;
  paused?: DefaultSlotText;
}

interface AsyncBoundaryProps<TData> {
  query: QueryLike<TData>;
  isEmpty?: (data: NonNullable<TData>) => boolean;
  children: ReactNode;
  className?: string;
  announcements?: Partial<Record<AsyncState, string>>;
  defaults?: AsyncBoundaryDefaults;
  "aria-label"?: string;
}

/**
 * Declarative async/query state boundary. Feed it a TanStack Query-like result
 * and it resolves the current state (`idle` | `pending` | `refetching` | `error`
 * | `empty` | `paused` | `success`) and renders the matching branch, with an
 * `aria-live` announcer and focus management for the error retry built in.
 *
 * Provide branches as `<AsyncBoundary.*>` slots. Any state without a slot uses a
 * sensible default (`pending`/`error`/`empty`/`paused`). Only `success` is
 * required — you can even skip slots entirely and pass children as the success
 * UI (bare-children shortcut).
 *
 * Slot children may be a node or a render function. `Success`/`Empty` receive
 * `data`, `Error` receives `(error, retry)`. JSX can't infer `TData` through a
 * static slot, so annotate the render-prop param:
 * `{(rows: NonNullable<typeof query.data>) => ...}`.
 *
 * @example Bare children (only success UI, defaults for the rest)
 * ```tsx
 * const users = useQuery({ queryKey: ["users"], queryFn: fetchUsers });
 *
 * <AsyncBoundary isEmpty={(u) => u.length === 0} query={users}>
 *   {(rows: User[]) => <UserTable rows={rows} />}
 * </AsyncBoundary>
 * ```
 *
 * @example Explicit slots
 * ```tsx
 * <AsyncBoundary isEmpty={(u) => u.length === 0} query={users}>
 *   <AsyncBoundary.Pending><TableSkeleton /></AsyncBoundary.Pending>
 *   <AsyncBoundary.Empty>
 *     <Empty>…add your first user…</Empty>
 *   </AsyncBoundary.Empty>
 *   <AsyncBoundary.Error>
 *     {(error: unknown, retry: () => void) => (
 *       <ErrorCard error={error} onRetry={retry} />
 *     )}
 *   </AsyncBoundary.Error>
 *   <AsyncBoundary.Success>
 *     {(rows: User[]) => <UserTable rows={rows} />}
 *   </AsyncBoundary.Success>
 * </AsyncBoundary>
 * ```
 *
 * @example Customize default copy without writing a full slot
 * ```tsx
 * <AsyncBoundary
 *   defaults={{
 *     pending: { title: "Loading users…" },
 *     empty: { title: "No users yet", description: "Invite your team." },
 *     error: { title: "Couldn't load users" }, // description ← error message
 *   }}
 *   isEmpty={(u) => u.length === 0}
 *   query={users}
 * >
 *   {(rows: User[]) => <UserTable rows={rows} />}
 * </AsyncBoundary>
 * ```
 */
function AsyncBoundary<TData>({
  query,
  isEmpty,
  children,
  className,
  announcements,
  defaults,
  "aria-label": ariaLabel,
}: AsyncBoundaryProps<TData>): ReactNode {
  const resolved = resolveState(query, isEmpty);
  const { state } = resolved;

  const { slots, hasAnySlot } = collectSlots(children);
  const hasErrorSlot = slots.has("error");
  const retryRef = useRef<HTMLButtonElement>(null);
  const liveRef = useRef<HTMLDivElement>(null);
  const prevState = usePrevious(state);
  // Read the latest announcements without turning them into an effect
  // dependency — only `state` transitions should trigger an announcement.
  const announcementsRef = useLatest(announcements);

  // Announce transitions via the live region (write to DOM, not React state,
  // so re-announcing an identical message still fires for screen readers).
  useEffect(() => {
    const node = liveRef.current;
    if (!node) {
      return;
    }

    // Skip the noisy "loaded" announcement on very first mount.
    if (
      state === "success" &&
      (prevState === undefined || prevState === "success")
    ) {
      return;
    }
    if (state === "idle") {
      node.textContent = "";
      return;
    }

    node.textContent = announcementsRef.current?.[state] ?? ANNOUNCEMENTS[state];
  }, [state, prevState, announcementsRef]);

  // Move focus to the retry button when a default error surfaces (once per transition).
  useEffect(() => {
    if (state === "error" && !hasErrorSlot) {
      retryRef.current?.focus();
    }
  }, [state, hasErrorSlot]);

  const announcer = (
    <div aria-atomic="true" aria-live="polite" className="sr-only" ref={liveRef} />
  );

  const wrap = (content: ReactNode) => (
    <div
      aria-label={ariaLabel}
      className={cn("relative flex min-h-0 min-w-0 flex-1 flex-col", className)}
      data-slot="async-boundary"
      data-state={state}
    >
      {announcer}
      {content}
    </div>
  );

  // Slot content or the built-in default for stateless branches.
  const slotOr = (slotState: AsyncState, fallback: ReactNode): ReactNode => {
    const slot = slots.get(slotState);
    return slot === undefined ? fallback : callNodeOrFn(slot);
  };

  // Narrowed once for the data/error-bearing branches; the `<Case>` render
  // functions below run lazily, so each only reads what its state guarantees.
  const data = "data" in resolved ? resolved.data : undefined;
  const error = "error" in resolved ? resolved.error : undefined;

  return wrap(
    <Switch on={resolved.state}>
      <Case value="pending">
        {() =>
          slotOr(
            "pending",
            <DefaultPending
              className={className}
              description={defaults?.pending?.description}
              title={defaults?.pending?.title}
            />
          )
        }
      </Case>
      <Case value="idle">{() => slotOr("idle", null)}</Case>
      <Case value="paused">
        {() =>
          slotOr(
            "paused",
            <DefaultPaused
              description={defaults?.paused?.description}
              title={defaults?.paused?.title}
            />
          )
        }
      </Case>
      <Case value="error">
        {() => {
          const slot = slots.get("error");
          return slot === undefined ? (
            <DefaultError
              description={defaults?.error?.description}
              error={error}
              retry={query.refetch}
              retryRef={retryRef}
              title={defaults?.error?.title}
            />
          ) : (
            callNodeOrFn(slot, error, query.refetch)
          );
        }}
      </Case>
      <Case value="empty">
        {() => {
          const slot = slots.get("empty");
          return slot === undefined ? (
            <DefaultEmpty
              description={defaults?.empty?.description}
              title={defaults?.empty?.title}
            />
          ) : (
            callNodeOrFn(slot, data)
          );
        }}
      </Case>
      <Case value="refetching">
        {() => (
          <>
            {renderSuccess(
              slots,
              hasAnySlot,
              children,
              data as NonNullable<TData>,
            )}
            <RefetchOverlay indicator={slots.get("refetching")} />
          </>
        )}
      </Case>
      <Case value="success">
        {() =>
          renderSuccess(slots, hasAnySlot, children, data as NonNullable<TData>)
        }
      </Case>
    </Switch>,
  );
}

function renderSuccess<TData>(
  slots: Map<AsyncState, NodeOrFn>,
  hasAnySlot: boolean,
  children: ReactNode,
  data: NonNullable<TData>,
): ReactNode {
  const slot = slots.get("success");
  if (slot !== undefined) {
    return callNodeOrFn(slot, data);
  }

  // Bare-children shortcut: no recognized slots → children are the success UI.
  if (!hasAnySlot) {
    return callNodeOrFn(children as NodeOrFn<[data: TData]>, data);
  }

  if (process.env.NODE_ENV !== "production") {
    console.warn(
      "[AsyncBoundary] Reached success state but no <AsyncBoundary.Success> slot was provided.",
    );
  }
  return null;
}

AsyncBoundary.Pending = Pending;
AsyncBoundary.Idle = Idle;
AsyncBoundary.Paused = Paused;
AsyncBoundary.Refetching = Refetching;
AsyncBoundary.Error = ErrorSlot;
AsyncBoundary.Empty = EmptySlot;
AsyncBoundary.Success = Success;

export {
  AsyncBoundary,
  resolveState,
  type AsyncBoundaryDefaults,
  type AsyncBoundaryProps,
  type AsyncState,
  type QueryLike,
};
